import { bodyRegionLayout, type BodyProfile } from '../../api/bodyProfile';

interface Props {
  profile: Pick<BodyProfile, 'height_cm' | 'weight_kg' | 'body_dims'>;
  className?: string;
}

/** Segmented 2D mannequin. Each slider deforms only its band. */
export function BodySilhouette({ profile, className }: Props) {
  const L = bodyRegionLayout(profile);
  const cx = 100;
  const headR = 16;
  const headCy = 28;
  const neckW = 10;
  const shoulderY = 62;
  const bustY = shoulderY + 28 * L.torso;
  const waistY = bustY + 36 * L.torso;
  const hipY = waistY + 34 * L.torso;
  const footY = Math.min(392, hipY + 118 * L.leg);

  const shoulderHW = 34 * L.shoulder;
  const bustHW = 30 * L.bust;
  const waistHW = 22 * L.waist;
  const hipHW = 30 * L.hip;
  const thighHW = 12 * L.hip;
  const calfHW = 7.5;

  const left = (y: number, hw: number) => `${cx - hw},${y}`;
  const right = (y: number, hw: number) => `${cx + hw},${y}`;

  const torso = [
    `M ${left(shoulderY, shoulderHW)}`,
    `C ${left(shoulderY + 8, shoulderHW)} ${left(bustY - 6, bustHW)} ${left(bustY, bustHW)}`,
    `C ${left(bustY + 10, bustHW * 0.92)} ${left(waistY - 8, waistHW)} ${left(waistY, waistHW)}`,
    `C ${left(waistY + 10, waistHW)} ${left(hipY - 6, hipHW)} ${left(hipY, hipHW)}`,
    `L ${right(hipY, hipHW)}`,
    `C ${right(hipY - 6, hipHW)} ${right(waistY + 10, waistHW)} ${right(waistY, waistHW)}`,
    `C ${right(waistY - 8, waistHW)} ${right(bustY + 10, bustHW * 0.92)} ${right(bustY, bustHW)}`,
    `C ${right(bustY - 6, bustHW)} ${right(shoulderY + 8, shoulderHW)} ${right(shoulderY, shoulderHW)}`,
    'Z',
  ].join(' ');

  const leg = (side: -1 | 1) => {
    const hipX = cx + side * hipHW * 0.42;
    const kneeY = hipY + (footY - hipY) * 0.48;
    const ankleY = footY - 6;
    const outer = side * thighHW;
    const inner = side * 3.2;
    return [
      `M ${hipX - outer},${hipY}`,
      `L ${hipX - inner},${hipY}`,
      `L ${hipX - side * 2.2},${kneeY}`,
      `L ${hipX - side * calfHW * 0.15},${ankleY}`,
      `L ${hipX + side * calfHW},${ankleY}`,
      `L ${hipX + side * (thighHW * 0.72)},${kneeY}`,
      'Z',
    ].join(' ');
  };

  return (
    <svg
      viewBox="0 0 200 400"
      className={className}
      style={{ transform: `scale(${L.heightScale})`, transformOrigin: 'center bottom' }}
      aria-hidden
    >
      <circle cx={cx} cy={headCy} r={headR} fill="#d4d4d8" />
      <rect
        x={cx - neckW / 2}
        y={headCy + headR - 2}
        width={neckW}
        height={shoulderY - (headCy + headR) + 6}
        rx={3}
        fill="#d4d4d8"
      />
      <path d={torso} fill="#a1a1aa" />
      <path d={leg(-1)} fill="#d4d4d8" />
      <path d={leg(1)} fill="#d4d4d8" />
    </svg>
  );
}
